import React, { useEffect, useRef, useState } from "react"
import { Animated, Pressable, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import EventCard from "./EventCard"
import { dismissControlOpacity } from "./eventsCatalog"

const DURATION_MS = 6000

const TutorEventSnackbar = ({
    event,
    onClose,
    onPrimaryAction,
    bottom = 70,
    right = 16,
}) => {
    const [hovered, setHovered] = useState(false)
    const fillAnim = useRef(new Animated.Value(1)).current
    const fillValueRef = useRef(1)

    // Track the live fill value so hover-out can resume from where it paused
    // instead of restarting the whole 6s countdown.
    useEffect(() => {
        const id = fillAnim.addListener(({ value }) => {
            fillValueRef.current = value
        })
        return () => fillAnim.removeListener(id)
    }, [fillAnim])

    // Reset the bar only when a NEW event takes the snackbar.
    useEffect(() => {
        if (!event) return
        fillAnim.setValue(1)
        fillValueRef.current = 1
    }, [event?.hash, fillAnim])

    // Run / pause / resume. Hover pauses (freeze remaining); leaving resumes
    // from the captured value so brushing the cursor can't keep it alive.
    useEffect(() => {
        if (!event) return undefined
        if (hovered) {
            fillAnim.stopAnimation((v) => {
                fillValueRef.current = v
            })
            return undefined
        }
        const remaining = Math.max(200, DURATION_MS * fillValueRef.current)
        const anim = Animated.timing(fillAnim, {
            toValue: 0,
            duration: remaining,
            useNativeDriver: false,
        })
        anim.start(({ finished }) => {
            if (finished) onClose?.()
        })
        return () => anim.stop()
    }, [event?.hash, hovered])

    if (!event) return null

    const widthInterpolate = fillAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ["0%", "100%"],
    })

    return (
        <Pressable
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
            style={[styles.snackbar, { bottom, right }]}
        >
            <EventCard
                event={event}
                onPrimaryAction={onPrimaryAction}
                onDismiss={null}
                compact
                now={event.startedAt}
            />
            <Pressable
                onPress={onClose}
                style={[
                    styles.close,
                    { opacity: dismissControlOpacity(hovered) },
                ]}
                hitSlop={8}
            >
                <TextM
                    color={theme.colors.greyAlt}
                    fontSize={14}
                    fontWeight="ExtraBold"
                >
                    ✕
                </TextM>
            </Pressable>
            <View style={styles.countdown}>
                <Animated.View
                    style={[styles.countdownFill, { width: widthInterpolate }]}
                />
            </View>
        </Pressable>
    )
}

const styles = StyleSheet.create({
    snackbar: {
        position: "absolute",
        zIndex: 45,
        width: 340,
        backgroundColor: theme.colors.white,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.22,
        shadowRadius: 16,
        elevation: 10,
        overflow: "hidden",
    },
    close: {
        position: "absolute",
        top: 6,
        right: 6,
        width: 22,
        height: 22,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 100,
    },
    countdown: {
        height: 3,
        backgroundColor: theme.colors.lighter,
    },
    countdownFill: {
        height: 3,
        backgroundColor: theme.colors.accent,
    },
})

export default TutorEventSnackbar
